import React, { forwardRef, useContext, useImperativeHandle } from 'react';
import isFunction from 'lodash/isFunction';
import useArrayField from '../hooks/useArrayField';
import Adapter from './Adapter';
import { ArrayFieldApiContext, ArrayFieldItemApiContext, ArrayFieldStateContext } from '../Context';
import useArrayFieldItem from '../hooks/useArrayFieldItem';
// eslint-disable-next-line import/no-cycle
import getFieldComponent from '../utils/getFieldComponent';
import useAdapter from '../hooks/useAdapter';

export default function ArrayField({ children, ...props }) {
  const adapter = useAdapter();
  const {
    arrayFieldApi,
    formFieldContext,
    formFieldProps: { inputProps, ...formFieldProps },
    arrayFieldState,
    render,
  } = useArrayField(props);

  const {
    field: { items },
  } = formFieldContext;

  if (children == null && items != null) {
    const FieldComponent = getFieldComponent(adapter, items);

    return render(
      <Adapter component="FormField" {...formFieldProps} context={formFieldContext}>
        <ArrayField.Items>
          <Adapter component="FormItem">
            <FieldComponent $ref="" />
            <ArrayField.Action name="remove">Remove item</ArrayField.Action>
          </Adapter>
        </ArrayField.Items>
        <ArrayField.Action name="add">Add item</ArrayField.Action>
      </Adapter>
    );
  }

  if (isFunction(children)) {
    return render(children({ arrayFieldApi, arrayFieldState }));
  }

  return render(children);
}

const ArrayFieldItem = forwardRef(({ children, ...props }, ref) => {
  const { arrayFieldItemApi, arrayFieldItemState, render } = useArrayFieldItem(props);

  useImperativeHandle(ref, () => ({
    reset: () => {
      arrayFieldItemApi.reset();
    },
  }));

  if (isFunction(children)) {
    return render(children({ arrayFieldItemApi, arrayFieldItemState }));
  }

  return render(children);
});

ArrayField.Items = function ArrayFieldItems({ children }) {
  const { items } = useContext(ArrayFieldStateContext);

  return items.map((item) => (
    <ArrayFieldItem key={item.key} item={item}>
      {children}
    </ArrayFieldItem>
  ));
};

ArrayField.Action = function ArrayFieldAction({ name, args = [], ...props }) {
  const arrayFieldApi = useContext(ArrayFieldApiContext);
  const arrayFieldItemApi = useContext(ArrayFieldItemApiContext);

  const useApi = arrayFieldItemApi ?? arrayFieldApi;

  const handleClick = () => {
    return isFunction(useApi[name]) ? useApi[name](...args) : null;
  };

  return <Adapter component="Button" type="button" onClick={handleClick} {...props} />;
};

ArrayField.Error = function ArrayFieldError({ children }) {
  const { $showError, error } = useContext(ArrayFieldStateContext);

  if (isFunction(children)) {
    return children($showError ? error : undefined);
  }

  return $showError ? error ?? null : null;
};
